/**
 * Shared 2-pass exclusion mask renderer for WebGPU post-processing effects.
 * Renders a red-channel mask where excluded objects should bypass effect processing.
 */

import * as THREE from 'three';
import { MeshBasicNodeMaterial, RendererUtils, SpriteNodeMaterial } from 'three/webgpu';

import type { WebGPURenderer } from '../../Renderer.js';

type RendererState = Parameters<typeof RendererUtils.resetRendererState>[1];

type MaterialRestoreEntry = {
  object: THREE.Object3D;
  material: THREE.Material | THREE.Material[];
};

export type ExclusionMaskRenderOptions = {
  renderer: WebGPURenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  excludedLayer: number;
  /** When provided, only excluded-layer objects passing this test are written to the mask. */
  shouldIncludeObject?: (object: THREE.Object3D) => boolean;
};

/**
 * Renders exclusion masks for post-processing compose steps.
 * Pass 1 primes depth; pass 2 writes white for excluded geometry.
 */
export class ExclusionMaskRenderer {
  protected maskTarget: THREE.RenderTarget | null = null;
  protected maskBlackMat: MeshBasicNodeMaterial | null = null;
  protected maskWhiteMat: MeshBasicNodeMaterial | null = null;
  protected maskWhiteMatNoDepthTest: MeshBasicNodeMaterial | null = null;
  protected spriteMaterialCache: Map<THREE.Texture | null, SpriteNodeMaterial> = new Map();

  private drawingBufferSizeCache = new THREE.Vector2();
  private rendererStateCache = {} as RendererState;
  private hiddenToRestore: THREE.Object3D[] = [];
  private materialRestoreEntries: MaterialRestoreEntry[] = [];
  private hasExclusions = false;

  /** Whether the last {@link renderMask} call produced a usable exclusion mask. */
  public getHasExclusions(): boolean {
    return this.hasExclusions;
  }

  /** Texture written by the most recent {@link renderMask} call. */
  public getExclusionMaskTexture(): THREE.Texture | null {
    return this.maskTarget?.texture ?? null;
  }

  /** Create or resize the mask render target to match renderer dimensions. */
  public updateMaskTarget(renderer: WebGPURenderer): void {
    const pixelRatio = renderer.getPixelRatio();
    const size = renderer.getSize(new THREE.Vector2());
    const width = Math.max(1, Math.floor(size.width * pixelRatio));
    const height = Math.max(1, Math.floor(size.height * pixelRatio));

    if (!this.maskTarget) {
      this.maskTarget = new THREE.RenderTarget(width, height, {
        format: THREE.RedFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
        generateMipmaps: false,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
      });
      // @ts-ignore
      this.maskTarget.texture.colorSpace = THREE.NoColorSpace;
    } else if (this.maskTarget.width !== width || this.maskTarget.height !== height) {
      this.maskTarget.setSize(width, height);
    }
  }

  /** Ensure mask materials exist (safe to call before first render). */
  public ensureMaskMaterials(): void {
    if (!this.maskBlackMat) {
      this.maskBlackMat = new MeshBasicNodeMaterial({ color: 0x000000 });
      this.maskBlackMat.depthTest = true;
      this.maskBlackMat.depthWrite = true;
      // Pass 1: prime depth only; colour stays black from clear.
      this.maskBlackMat.colorWrite = false;
    }
    if (!this.maskWhiteMat) {
      this.maskWhiteMat = new MeshBasicNodeMaterial({ color: 0xffffff, transparent: true, opacity: 1.0 });
      this.maskWhiteMat.depthTest = true;
      this.maskWhiteMat.depthWrite = false;
    }
    if (!this.maskWhiteMatNoDepthTest) {
      this.maskWhiteMatNoDepthTest = new MeshBasicNodeMaterial({ color: 0xffffff, transparent: true, opacity: 1.0 });
      this.maskWhiteMatNoDepthTest.depthTest = false;
      this.maskWhiteMatNoDepthTest.depthWrite = false;
    }
  }

  /**
   * Render the exclusion mask for the current frame.
   * @returns true when the mask contains excluded pixels this frame.
   */
  public renderMask(options: ExclusionMaskRenderOptions): boolean {
    const { renderer, scene, camera, excludedLayer, shouldIncludeObject } = options;

    this.ensureMaskMaterials();
    this.updateMaskTarget(renderer);

    const originalOverride = scene.overrideMaterial;
    const originalBackground = scene.background;
    const originalEnvironment = scene.environment;
    const originalAutoClear = renderer.autoClear;
    const currentMask = camera.layers.mask;
    const excludedLayerMask = 1 << excludedLayer;

    let rendererState: RendererState | undefined;

    try {
      rendererState = RendererUtils.resetRendererState(renderer, this.rendererStateCache);

      scene.background = null;
      scene.environment = null;
      renderer.autoClear = false;

      renderer.setRenderTarget(this.maskTarget);
      if (this.maskTarget) {
        renderer.setViewport(0, 0, this.maskTarget.width, this.maskTarget.height);
      }
      renderer.setScissorTest(false);
      renderer.setClearColor(0x000000, 0);
      renderer.clearColor();
      renderer.clearDepth();

      // Pass 1: prime depth with scene excluding the excluded layer.
      if (this.maskBlackMat) {
        scene.overrideMaterial = this.maskBlackMat;
        renderer.render(scene, camera);
      }

      // Pass 2: render excluded objects white (depth-tested against pass 1).
      scene.overrideMaterial = null;
      camera.layers.mask = currentMask | excludedLayerMask;

      this.hiddenToRestore.length = 0;
      this.materialRestoreEntries.length = 0;

      try {
        scene.traverseVisible((object: THREE.Object3D) => {
          if (object instanceof THREE.Light) return;

          const isExcludedObject = (object.layers.mask & excludedLayerMask) !== 0;
          if (!isExcludedObject && 'material' in object) {
            object.visible = false;
            this.hiddenToRestore.push(object);
            return;
          }

          if (shouldIncludeObject && isExcludedObject && !shouldIncludeObject(object)) {
            object.visible = false;
            this.hiddenToRestore.push(object);
            return;
          }

          const objectAny = object as { material?: THREE.Material | THREE.Material[]; isSprite?: boolean };
          if (!objectAny.material) return;

          const mat = objectAny.material;
          const singleMat = Array.isArray(mat) ? mat[0] : mat;
          if (singleMat?.transparent && (singleMat as { opacity?: number }).opacity !== undefined && (singleMat as { opacity?: number }).opacity! < 0.01) return;

          if (objectAny.isSprite) {
            const sprite = object as THREE.Sprite;
            const originalMat = sprite.material;
            const textureKey = originalMat.map ?? null;

            let spriteMat = this.spriteMaterialCache.get(textureKey);
            if (!spriteMat) {
              spriteMat = new SpriteNodeMaterial({
                ...(textureKey ? { map: textureKey } : {}),
                color: 0xffffff,
                depthTest: false,
                depthWrite: false,
                transparent: false,
                alphaTest: 0.0001,
              });
              this.spriteMaterialCache.set(textureKey, spriteMat);
            }

            this.materialRestoreEntries.push({ object, material: originalMat });
            sprite.material = spriteMat;
            return;
          }

          const originalMat = objectAny.material;

          // TSL viewport grid cannot be rendered safely to RedFormat RTT (invalid shaders / full quad).
          if (object.userData.isViewportGrid === true) {
            object.visible = false;
            this.hiddenToRestore.push(object);
            return;
          }

          this.materialRestoreEntries.push({ object, material: originalMat });

          const materials = Array.isArray(originalMat) ? originalMat : [originalMat];
          const minOpacity = Math.min(...materials.map(m => (m as { opacity?: number })?.opacity ?? 1.0));
          const needsNoDepthTest = materials.some(m => m?.depthTest === false);
          if (needsNoDepthTest) {
            this.maskWhiteMatNoDepthTest!.opacity = minOpacity;
            objectAny.material = this.maskWhiteMatNoDepthTest!;
          } else {
            this.maskWhiteMat!.opacity = minOpacity;
            objectAny.material = this.maskWhiteMat!;
          }
        });

        renderer.render(scene, camera);
      } finally {
        for (const { object, material } of this.materialRestoreEntries) {
          if ('material' in object) {
            (object as { material: THREE.Material | THREE.Material[] }).material = material;
          }
        }
        for (const object of this.hiddenToRestore) {
          object.visible = true;
        }
        this.materialRestoreEntries.length = 0;
        this.hiddenToRestore.length = 0;
      }

      this.hasExclusions = true;
      return true;
    } finally {
      camera.layers.mask = currentMask;

      scene.overrideMaterial = originalOverride;
      scene.background = originalBackground;
      scene.environment = originalEnvironment;
      renderer.autoClear = originalAutoClear;

      if (rendererState) {
        RendererUtils.restoreRendererState(renderer, rendererState);
        this.restoreRendererViewport(renderer);
      }
    }
  }

  /** Restore the full logical canvas viewport after an offscreen mask pass. */
  private restoreRendererViewport(renderer: WebGPURenderer): void {
    renderer.getSize(this.drawingBufferSizeCache);
    renderer.setViewport(0, 0, this.drawingBufferSizeCache.width, this.drawingBufferSizeCache.height);
  }

  /** Clear the has-exclusions flag when no mask was rendered this frame. */
  public clearHasExclusions(): void {
    this.hasExclusions = false;
  }

  public dispose(): void {
    this.maskTarget?.dispose();
    this.maskTarget = null;
    this.maskBlackMat?.dispose();
    this.maskBlackMat = null;
    this.maskWhiteMat?.dispose();
    this.maskWhiteMat = null;
    this.maskWhiteMatNoDepthTest?.dispose();
    this.maskWhiteMatNoDepthTest = null;

    for (const mat of this.spriteMaterialCache.values()) {
      mat?.dispose();
    }
    this.spriteMaterialCache.clear();

    this.hasExclusions = false;
  }
}
